import { UserProfile } from 'fitbeat.gym-core';
import { ISubscriptionPaymentSummary } from 'fitbeat.models';
import React, { FunctionComponent, PureComponent } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Button, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import Sizes from '../../sizes';
import Theme from '../../theme';
import { FormError, TextInput } from '../../ui/components';
import { isRequired } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import {
  applyCouponPressed as applyCouponPressedActionCreator,
  haveCouponPressed as haveCouponPressedActionCreator,
  removeCouponPressed as removeCouponPressedActionCreator,
} from '../redux/payments-actions';
import {
  couponToApplySelector,
  couponVerificationFailedSelector,
  getPaymentSubscriptionSummarySelector,
  getRemoveCouponInProgressSelector,
  haveCouponPressedSelector,
  verifyingCouponInProgressSelector,
} from '../redux/payments-selectors';

interface IProps {
  haveCouponPressed: boolean;
  verifyingCouponInProgress: boolean;
  haveCouponPressedAction: () => void;
  subscriptionSummary: ISubscriptionPaymentSummary | null;
  applyCouponPressedAction: (couponCode: string, planId: string | undefined) => void;
  t: (key: string, options?: any) => string;
  handleSubmit: (fn: (values: { couponCode: string }) => void) => void;
  couponVerificationFailed: boolean;
  couponToApply: string;
  userId: string;
  removedCouponPressed: () => void;
  isRemovingCouponInProgress: boolean;
}

interface ICaptureCouponProps extends IProps {
  applyCoupon: (values: { couponCode: string }) => void;
}

const CaptureCoupon: FunctionComponent<ICaptureCouponProps> = ({
 t,
 verifyingCouponInProgress,
 handleSubmit,
 applyCoupon,
}) => (
  <View style={styles.captureCouponContainer}>
    <View style={styles.couponInputField}>
      <Field
        trim
        showLabel={false}
        name={'couponCode'}
        placeholder={t('couponEnterCode')}
        component={TextInput}
        returnKeyType={'done'}
        validate={isRequired}
      />
    </View>
    <Button
      buttonType={ButtonTypes.OUTLINE}
      disabled={verifyingCouponInProgress}
      containerStyle={styles.applyCouponButton}
      isLoading={verifyingCouponInProgress}
      title={t('couponApplyButton')}
      onPress={handleSubmit(applyCoupon)}
    />
  </View>
);

class ManageCoupon extends PureComponent<IProps> {
  public applyCoupon = (values: { couponCode: string }) => {
    const { applyCouponPressedAction, subscriptionSummary } = this.props;
    const { couponCode } = values;
    applyCouponPressedAction(couponCode, subscriptionSummary?.planId);
  }

  public render() {
    const {
      haveCouponPressed,
      t,
      haveCouponPressedAction,
      couponVerificationFailed,
      couponToApply,
      subscriptionSummary,
      removedCouponPressed,
      isRemovingCouponInProgress,
    } = this.props;

    const couponPresent = subscriptionSummary && subscriptionSummary.coupon;

    return (
      <View style={styles.container}>

        {couponVerificationFailed &&
          <FormError
            error={{ message: t('couponNotValidError', { code: couponToApply }) }}
            iconSize={defaultScale(30)}
          />
        }

        {haveCouponPressed &&
          <CaptureCoupon {...this.props} applyCoupon={this.applyCoupon}/>
        }

        {couponPresent &&
          <>
            {isRemovingCouponInProgress &&
              <Text style={[styles.textButton, { textDecorationLine: 'none' }]}>{t('removingCoupon')}</Text>
            }
            {!isRemovingCouponInProgress &&
              <TouchableOpacity onPress={removedCouponPressed}>
                <Text style={styles.textButton}>{t('removeCoupon')}</Text>
              </TouchableOpacity>
            }
          </>
        }

        {!couponPresent && !haveCouponPressed &&
          <TouchableOpacity onPress={haveCouponPressedAction}>
            <Text style={styles.textButton}>{t('haveCoupon')}</Text>
          </TouchableOpacity>
        }
      </View>
    );
  }
}

const mapStateToProps = (state: IAppStore) => ({
  haveCouponPressed: haveCouponPressedSelector(state),
  verifyingCouponInProgress: verifyingCouponInProgressSelector(state),
  couponVerificationFailed: couponVerificationFailedSelector(state),
  couponToApply: couponToApplySelector(state),
  subscriptionSummary: getPaymentSubscriptionSummarySelector(state),
  isRemovingCouponInProgress: getRemoveCouponInProgressSelector(state),
});

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const { userId } = ownProps;
  return {
    haveCouponPressedAction: () => dispatch(haveCouponPressedActionCreator()),
    applyCouponPressedAction: (couponCode: string, planId: string | undefined) =>
      dispatch(applyCouponPressedActionCreator(couponCode, planId, userId)),
    removedCouponPressed: () => dispatch(removeCouponPressedActionCreator(userId)),
  };
};

export default reduxForm({
  form: 'addCoupon',
  destroyOnUnmount: true,
})(connect(mapStateToProps, mapDispatchToProps)(translate(['payments'], { wait: true })(ManageCoupon)));

const styles = StyleSheet.create({
  container: {
    minHeight: defaultScale(Sizes.InputHeight),
    justifyContent: 'center',
    marginTop: defaultScale(10),
  },
  captureCouponContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  couponInputField: {
    flex: 2,
  },
  applyCouponButton: {
    flex: 1,
    marginLeft: defaultScale(5),
    marginVertical: defaultScale(5),
  },
  textButton: {
    ...Theme.textStyle.bodyCopyReg,
    textDecorationLine: 'underline',
    textAlign: 'right',
    alignItems: 'center',
  },
});
